import PortalDialog, {PortalDialogProperties, PortalDialogState} from "../PortalDialog";
import React, {ReactNode} from "react";
import {Dialog} from "primereact/dialog";
import PortalDialogType from "../../PortalDialogType";
import {Button} from "primereact/button";
import Parameter from "../../parameter/Parameter";
import PortalDocument from "../../../document/PortalDocument";
import MimeTypeFilter from "../../../document/mimetype/MimeTypeFilter";
import MimeTypes from "../../../document/mimetype/MimeTypes";
import {WebServiceTypes} from "@softvision/webpdf-wsclient-9-typescript";
import PortalDocumentManager from "../../../document/PortalDocumentManager";
import DialogDefault from "../../parameter/PortalDialogDefault";
import FormsExtractionDialogFields from "../../field/forms/FormsExtractionDialogFields";
import MimeType from "../../../document/mimetype/MimeType";
import {Dropdown} from "primereact/dropdown";
import Message from "../../../../shared/log/Message";
import {MessageType} from "../../../../shared/log/MessageInterface";
import {
	AdminWebserviceStatus,
	BaseToolbox,
	FormDocument,
	FormsExport,
	FormsFormat,
	Functions,
	Information,
	MetadataFormsFormat,
	MetadataInformation,
	Security,
	Settings,
	SettingsInterface,
	SignatureEditProfile,
	ToolboxFormsForms
} from "@softvision/webpdf-wsclient-9-typescript/lib/generated-sources";
import PortalDialogSecurity, {Intention} from "../../PortalDialogSecurity";
import Translate from "../../../../shared/translation/Translate";
import Helper from "../../../../shared/components/Helper";
import Icon from "../../../../shared/components/Icon";
import FunctionHelper from "../../../../shared/helper/FunctionHelper";
import {SelectItem} from "primereact/selectitem";

export interface FormsExtractionDialogState extends PortalDialogState<ToolboxFormsForms> {
	formType: MetadataFormsFormat;
}

export class FormsExtractionDialogDefinition extends PortalDialog<ToolboxFormsForms,
	PortalDialogProperties<ToolboxFormsForms>, FormsExtractionDialogState> {
	constructor(props: any) {
		super(props);

		this.state = {
			...this.state,
			formType: MetadataFormsFormat.None,
			fieldErrors: new Map<FormsExtractionDialogFields, string>()
		};
	}

	public static isEnabled(portalInfo: Information): boolean {
		return FunctionHelper.applyValue(
			portalInfo.webserviceStatus?.webservices?.toolbox?.enabled, AdminWebserviceStatus.getEnabledDefault()
		) && FunctionHelper.applyValue(
			portalInfo.functionsStatus?.editForms, Functions.getEditFormsDefault()
		);
	}

	public static isActive(
		selectedDocuments: Array<PortalDocument>, portalInfo: Information, signatureEditProfile: SignatureEditProfile
	): boolean {
		let portalDocument: PortalDocument | undefined = selectedDocuments[0];
		let mimeTypes: Array<MimeType> = [];

		for (let selectedDocument of selectedDocuments) {
			mimeTypes.push(selectedDocument.getMimeType());

			if (selectedDocument.isLocked()) {
				return false;
			}

			if (!PortalDialogSecurity.checkRestrictions(
				PortalDialogType.EXTRACT_FORMS, signatureEditProfile, Intention.reading, selectedDocument
			)) {
				return false;
			}

			if (!selectedDocument.isEditable({checkArchive: false})) {
				return false;
			}

			if (FunctionHelper.applyValue(
				selectedDocument.metadata?.form?.field, FormDocument.getFieldDefault()
			).length === 0) {
				return false;
			}
		}

		return this.isEnabled(portalInfo) && selectedDocuments.length === 1 &&
			MimeTypeFilter.checkSelection(mimeTypes, [MimeTypes.PDF]) &&
			portalDocument.metadata?.information?.formType !== MetadataFormsFormat.None;
	}

	public renderContents(): ReactNode {
		let parameter: ToolboxFormsForms | undefined = this.state.parameter?.data;

		if (!FunctionHelper.exists(parameter)) {
			return null;
		}

		let formats: Array<SelectItem> = [
			{
				label: Translate.value("extract_forms.format.xml", {ns: "dialog"}),
				value: FormsFormat.Xml
			}
		];

		if (this.state.formType === MetadataFormsFormat.AcroForm) {
			formats = formats.concat([
				{
					label: Translate.value("extract_forms.format.fdf", {ns: "dialog"}),
					value: FormsFormat.Fdf
				},
				{
					label: Translate.value("extract_forms.format.xfdf", {ns: "dialog"}),
					value: FormsFormat.Xfdf
				}
			]);
		}

		if (this.state.formType === MetadataFormsFormat.DynamicXFA ||
			this.state.formType === MetadataFormsFormat.StaticXFA) {
			formats = formats.concat([
				{
					label: Translate.value("extract_forms.format.xdp", {ns: "dialog"}),
					value: FormsFormat.Xdp
				}
			]);
		}

		return (
			<>
				<fieldset className="p-col-12 p-grid">
					<div className="p-col-12 p-grid"
						 hidden={!this.state.fields?.contains(FormsExtractionDialogFields.FORMAT)}>
						<label className="p-col-12 p-sm-4" htmlFor="control-format">
							<Translate {...this.props} data="extract_forms.format.label" namespace="dialog"/>
						</label>
						<div className="p-col-12 p-sm-8 p-grid">
							<Dropdown inputId="control-format"
									  value={FunctionHelper.applyValue(
										  parameter?._export?.format, FormsExport.getFormatDefault()
									  )}
									  onChange={(e) => this.onChange(
										  FormsExtractionDialogFields.FORMAT, e.value
									  )}
									  appendTo={document.getElementById("root")} options={formats}/>
						</div>
					</div>
				</fieldset>
			</>
		);
	}

	public renderDialog(): ReactNode {
		let classList: Array<string> = ["export-forms-dialog"];
		if (this.state.submitted) {
			classList.push("submitted");
		}

		return (
			<Dialog className={classList.join(" ")} visible={this.props.visible} onHide={this.hide} resizable={false}
					closeOnEscape={true} header={this.renderHeader()} footer={this.renderFooter()}
					appendTo={document.getElementById("root")}>
				<div className="p-grid">
					{this.renderError()}
					<Helper {...this.props}>
						<Translate {...this.props} data={"dialog.extract_forms.general"} namespace={"help"}
								   markdown={true}/>
					</Helper>
					{this.renderContents()}
				</div>
			</Dialog>
		);
	}

	protected onChange(field: FormsExtractionDialogFields, value: any): void {
		let parameter: ToolboxFormsForms = this.state.parameter!.data;

		switch (field) {
			case FormsExtractionDialogFields.FORMAT:
				parameter._export!.format = value;
				break;
		}

		this.setState({
			parameter: this.state.parameter
		});
	}

	protected renderHeader(): ReactNode {
		return (
			<div>
				<h1>
					<Icon icon={"icon-forms-export small"}/>
					<Translate {...this.props} data="extract_forms.title" namespace="dialog"/>
				</h1>
				<Button icon="icon icon-close" onClick={() => this.hide()}/>
				{this.renderHelpButton()}
			</div>
		);
	}

	protected isEditPasswordNeeded(portalDocument: PortalDocument): boolean {
		return super.isEditPasswordNeeded(portalDocument) && !FunctionHelper.applyValue(
			portalDocument.metadata?.security?.canExtractContent, Security.getCanExtractContentDefault()
		);
	}

	/**
	 * Handle showing/initializing the dialog
	 */
	protected async onShow(): Promise<void> {
		await super.onShow();

		let parameter: ToolboxFormsForms | undefined = this.state.parameter?.data;
		let portalDocument: PortalDocument = this.props.context.documentManager.getSelection()[0];

		if (!FunctionHelper.exists(parameter)) {
			return;
		}

		let formType: MetadataFormsFormat = FunctionHelper.applyValue(
			portalDocument.metadata?.information?.formType, MetadataInformation.getFormTypeDefault()
		);
		parameter!._export!.fdfFileName = portalDocument.getDocumentFile()?.fileName!;

		if (
			(
				parameter?._export?.format === FormsFormat.Xdp &&
				formType !== MetadataFormsFormat.DynamicXFA &&
				formType !== MetadataFormsFormat.StaticXFA
			) ||
			(
				(
					parameter?._export?.format === FormsFormat.Fdf ||
					parameter?._export?.format === FormsFormat.Xfdf
				) &&
				formType !== MetadataFormsFormat.AcroForm
			)
		) {
			parameter!._export.format = FormsFormat.Xml
		}

		this.setState({
			parameter: this.state.parameter,
			defaultParameter: this.state.parameter?.clone(),
			formType: formType
		});
	};

	protected async submit(): Promise<void> {
		await super.submit();

		let toolboxOperation: BaseToolbox = BaseToolbox.fromJson({
			forms: this.state.parameter!.data.toJson()
		});

		let operationData: Array<BaseToolbox> = [toolboxOperation];
		let documentManager: PortalDocumentManager = this.props.context.documentManager;
		let selection: Array<PortalDocument> = documentManager.getSelection();

		for (let portalDocument of selection) {
			try {
				await this.props.context.session.executeRequest({
					webServiceType: WebServiceTypes.TOOLBOX,
					document: portalDocument,
					operation: operationData,
					settings: Settings.fromJson({
						pdfa: {
							pdfaEditProfile: this.props.pdfaEditProfile
						},
						signature: {
							signatureEditProfile: this.props.signatureEditProfile
						},
						compress: {
							compressProfile: this.props.compressProfile,
							compressObjects: {
								objectStreamSize: this.props.objectStreamSize
							}
						}
					} as SettingsInterface)
				});
			} catch (exception: any) {
				if (await this.onError(exception)) {
					await this.submit();
				}
				return;
			}

			let message: Message = new Message("success.toolbox.forms.export", MessageType.SUCCESS);
			message.context = {filename: '"' + portalDocument.getFileName() + '" '};
			this.props.context.getPage().addMessage(message);
			this.hide();
		}
	}

	protected getDialogType(): PortalDialogType {
		return PortalDialogType.EXTRACT_FORMS;
	}

	protected getDefaultParameter(): Parameter<ToolboxFormsForms> {
		return DialogDefault.getParameter(
			this.getDialogType(), this.props.context.documentManager.getSelection()
		);
	}
}

const FormsExtractionDialog = React.memo(FormsExtractionDialogDefinition,
	(
		prevProps: Readonly<PortalDialogProperties<ToolboxFormsForms>>,
		nextProps: Readonly<PortalDialogProperties<ToolboxFormsForms>>
	) => {
		if (!nextProps.visible && !prevProps.visible) {
			return true
		}

		return false;
	}
);

export default FormsExtractionDialog;